"use client";

import { useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useUniversalAuthOptional } from "@wtc/ui";
import { Home, RefreshCw, AlertTriangle, Wifi, Server, ArrowLeft, Phone, Mail } from "lucide-react";
import { reportClientError } from "@/lib/observability/client-error-reporter";

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  // Boundary page: may render outside the provider tree. Non-throwing variant
  // so the error page never crashes when SimpleAuthProvider isn't mounted.
  const { appUser, isAuthenticated } = useUniversalAuthOptional();
  const userType = appUser?.role;

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.error("Application error:", error);
    }
    reportClientError({
      message: error.message || "Application error",
      name: error.name,
      stack: error.stack,
      digest: error.digest,
      context: { boundary: "app/error.tsx" },
    });
  }, [error, userType]);

  const getErrorType = () => {
    if (error.message.includes("fetch")) return "network";
    if (error.message.includes("database") || error.message.includes("supabase")) return "database";
    if (error.message.includes("auth") || error.message.includes("session"))
      return "authentication";
    return "general";
  };

  const errorType = getErrorType();

  const getErrorDetails = () => {
    switch (errorType) {
      case "network":
        return {
          title: "Connection Error",
          description: "Unable to connect to the server. Please check your internet connection.",
          icon: Wifi,
          color: "text-orange-600 dark:text-orange-400",
          bgColor: "bg-orange-50 dark:bg-orange-950/40",
          suggestions: [
            "Check your internet connection",
            "Try refreshing the page",
            "Wait a moment and try again",
          ],
        };
      case "database":
        return {
          title: "Database Error",
          description: "There was an issue accessing the database. Our team has been notified.",
          icon: Server,
          color: "text-red-600 dark:text-red-400",
          bgColor: "bg-red-50 dark:bg-red-950/40",
          suggestions: [
            "Try refreshing the page",
            "Check back in a few minutes",
            "Contact support if the issue persists",
          ],
        };
      case "authentication":
        return {
          title: "Authentication Error",
          description: "There was an issue with your login session. Please sign in again.",
          icon: AlertTriangle,
          color: "text-yellow-600 dark:text-yellow-400",
          bgColor: "bg-yellow-50 dark:bg-yellow-950/40",
          suggestions: [
            "Sign out and sign back in",
            "Clear your browser cache",
            "Try a different browser",
          ],
        };
      default:
        return {
          title: "Something Went Wrong",
          description: "An unexpected error occurred. Our team has been notified.",
          icon: AlertTriangle,
          color: "text-red-600 dark:text-red-400",
          bgColor: "bg-red-50 dark:bg-red-950/40",
          suggestions: [
            "Try refreshing the page",
            "Check back in a few minutes",
            "Contact support if the issue persists",
          ],
        };
    }
  };

  const errorDetails = getErrorDetails();
  const Icon = errorDetails.icon;

  const getQuickActions = () => {
    if (!isAuthenticated) {
      return [
        {
          title: "Sign In",
          description: "Return to the login page",
          href: "/",
          icon: Home,
          color: "text-blue-600 dark:text-blue-400",
          bgColor: "bg-blue-50 dark:bg-blue-950/40",
        },
      ];
    }

    if (userType === "wtc_employee") {
      return [
        {
          title: "Dashboard",
          description: "Return to your main dashboard",
          href: "/dashboard",
          icon: Home,
          color: "text-blue-600 dark:text-blue-400",
          bgColor: "bg-blue-50 dark:bg-blue-950/40",
        },
        {
          title: "My Tickets",
          description: "View your assigned tickets",
          href: "/tickets/my-tickets",
          icon: Home,
          color: "text-orange-600 dark:text-orange-400",
          bgColor: "bg-orange-50 dark:bg-orange-950/40",
        },
      ];
    }

    // Client user
    return [
      {
        title: "Client Dashboard",
        description: "Return to your client dashboard",
        href: "/contacts/dashboard",
        icon: Home,
        color: "text-blue-600 dark:text-blue-400",
        bgColor: "bg-blue-50 dark:bg-blue-950/40",
      },
    ];
  };

  const quickActions = getQuickActions();

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <Image
                src="/logo_long.svg"
                alt="West Tennessee Consulting Hub"
                width={200}
                height={48}
                className="h-12 w-auto mx-auto mb-2"
              />
              <div
                className={`mx-auto p-3 ${errorDetails.bgColor} rounded-full w-12 h-12 flex items-center justify-center`}
              >
                <Icon className={`h-6 w-6 ${errorDetails.color}`} />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold text-card-foreground">
              {errorDetails.title}
            </CardTitle>
            <CardDescription className="text-lg text-gray-600 dark:text-gray-400">
              {errorDetails.description}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Error Details */}
            <div className="text-center">
              <p className="text-gray-500 mb-4">Error ID: {error.digest || "unknown"}</p>
              <p className="text-sm text-gray-400">
                {isAuthenticated
                  ? `Welcome back, ${userType === "wtc_employee" ? "employee" : "client"}!`
                  : "Please sign in to access the system."}
              </p>
            </div>

            {/* Quick Actions */}
            <div>
              <h3 className="text-lg font-semibold text-card-foreground mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quickActions.map((action, index) => {
                  const ActionIcon = action.icon;
                  return (
                    <Link key={index} href={action.href}>
                      <Card
                        className={`cursor-pointer transition-all duration-200 hover:shadow-md ${action.bgColor}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${action.bgColor}`}>
                              <ActionIcon className={`h-5 w-5 ${action.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-card-foreground text-sm">
                                {action.title}
                              </h4>
                              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{action.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Troubleshooting Steps */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-card-foreground mb-4">
                Troubleshooting Steps
              </h3>
              <div className="space-y-3">
                {errorDetails.suggestions.map((suggestion, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-card-50 rounded-lg">
                    <div className="w-2 h-2 bg-card-400 rounded-full"></div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{suggestion}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
              <Button onClick={reset} className="flex-1" variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>

              <Link href="/" className="flex-1">
                <Button className="w-full" variant="outline">
                  <Home className="h-4 w-4 mr-2" />
                  Go Home
                </Button>
              </Link>
            </div>

            {/* Contact Support */}
            <div className="text-center pt-4 border-t">
              <p className="text-sm text-gray-500 mb-3">Still experiencing issues?</p>
              <div className="flex justify-center gap-4">
                <a
                  href="tel:+1-555-0123"
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <Phone className="h-4 w-4" />
                  Call Support
                </a>
                <a
                  href="mailto:support@wtc.com"
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <Mail className="h-4 w-4" />
                  Email Support
                </a>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Include Error ID: {error.digest || "unknown"} when contacting support
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
